<template>
  <div
    :class="['phoenix-keyboard-wrapper', `keyboard-${keyboardType}`, isChromeOS ? 'chrome-os' : '']"
    id="phoenix-keyboard-wrapper"
  >
    <div :class="['StandardKeyboard', { outline: showOutline, disabled: keyboardLocked }]">
      <div v-for="row in displayKeys" :key="row.name" :class="[`row ${row.name}`, theme]">
        <component
          :is="getKwtKey(key)"
          v-for="key in row.keys"
          :key="key.name"
          :name="key.name"
          :character="key.character"
          :alt="key.alt"
          :shift="key.shift"
          :hold-hand="key.hold"
          :row="row.name"
          :class="{ tablet: ipadWithoutExternalKeyboard }"
        ></component>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapGetters, mapState } from 'vuex';
import BaseKeyboard from 'mixins/BaseKeyboard';
import { keyLayout } from '@/constants/standard-keyboard';
import StandardKey from './keys/StandardKey.vue';
import HoldKey from './keys/HoldKey.vue';

const keyboard = createNamespacedHelpers('keyboard');

const keys = {
  standard: StandardKey,
  hold: HoldKey
};

export default {
  components: {
    ...keys
  },

  mixins: [BaseKeyboard],

  computed: {
    ...mapState('application', {
      isChromeOS: state => state.device.isChromeOS
    }),
    ...mapGetters('application', ['ipadWithoutExternalKeyboard']),

    displayKeys() {
      if (!this.hideNumberRow) return keyLayout;
      return keyLayout.filter(row => row.name !== 'rowNumber');
    }
  },

  data() {
    return {
      processKeyPress: null
    };
  },

  created() {
    if (this.isTouchHold) {
      this.processKeyPress = this.processTouchHoldKey;
      return;
    }

    this.processKeyPress = this.processStandardKey;
  },

  methods: {
    ...keyboard.mapMutations(['setCapsLock']),

    addKeyListeners() {
      window.addEventListener('keydown', this.handleKeyDown);
      window.addEventListener('keyup', this.handleKeyUp);

      if (this.isTouchHold && !this.device.isMobile) {
        window.addEventListener('keydown', this.handleHoldKeyDown);
        window.addEventListener('keyup', this.handleHoldKeyUp);
      }
    },

    removeKeyListeners() {
      window.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('keydown', this.handleHoldKeyDown);

      window.removeEventListener('keyup', this.handleKeyUp);
      window.removeEventListener('keyup', this.handleHoldKeyUp);
    },

    getKwtKey(key) {
      if (this.isOneHandedTypingHandActivity) {
        return 'standard';
      }

      if (this.isTouchHold && key.hold && this.targetKey === key.name) {
        return 'hold';
      }

      return 'standard';
    },

    processTouchHoldKey(key, { withShift }) {
      if (this.keyboardLocked && (this.isOneHandedTypingHandActivity || !this.capsLock)) return;
      this.attemptKey({ key, withShift });
    },

    processStandardKey(key, { withCmd, withShift }) {
      this.attemptKey({ key, withCmd, withShift });
    },

    doNotHandleKey(event) {
      const { key } = event;
      const withCmdKey = event.metaKey;
      const withShiftKey = event.shiftKey;
      const isZoomingWithKeyboard = withCmdKey && ['-', '='].includes(key);

      return (
        (key !== 'Backspace' &&
          (this.keyboardDisabled ||
            isZoomingWithKeyboard ||
            key === 'CapsLock' ||
            this.keyNotUsedInActivity(key, event) ||
            this.keyIsPressed({ event }))) ||
        (key === 'Tab' && withShiftKey)
      );
    },

    isShiftKeyTarget(event) {
      return this.targetKey.includes('shift') && this.isShiftKey(event.key);
    },

    handleHoldKeyDown(event) {
      // event.preventDefault();

      if (this.keyNotUsedInActivity(event.key, event)) return;

      if ((event.key === this.targetKey || this.isShiftKeyTarget(event)) && this.keyboardLocked) {
        this.pressHoldKey();
        this.addToActiveKeys({ event });
      }
    },

    handleHoldKeyUp(event) {
      // event.preventDefault();

      if (event.key === this.targetKey || this.isShiftKeyTarget(event)) {
        this.releaseHoldKey();
        this.removeFromActiveKeys({ event });
      }
    },

    handleKeyDown(event) {
      const { key, location = '' } = event;

      let isCapsLock = false;
      if (event.getModifierState && event.getModifierState('CapsLock')) {
        isCapsLock = true;
      }

      if (isCapsLock !== this.capsLock) {
        this.setCapsLock(isCapsLock);
      }

      if (this.doNotHandleKey(event)) return;

      const normalizedKey = this.normalizeKey(key, location);
      this.processKeyPress(normalizedKey, { withCmd: event.metaKey, withShift: event.shiftKey });

      // don't add shift to activeKey array. shift is a 'free' key
      if (!this.isShiftKey(key)) {
        // prevent default only if key is used to complete the activity
        const handleAllNotNatively = this.keyCodesToNotHandleNatively.includes('all');
        const handleKeyNotNatively = this.keyCodesToNotHandleNatively.includes(event.keyCode);
        const handleKeyNatively = this.keyCodesToHandleNatively.includes(event.keyCode);
        const isSpaceBar = event.keyCode === 32;
        if (
          isSpaceBar ||
          (this.attemptedKey?.isCorrect &&
            (handleAllNotNatively || handleKeyNotNatively) &&
            !handleKeyNatively)
        ) {
          event.preventDefault();
        }
        this.addToActiveKeys({ event });
      }
    },

    handleKeyUp(event) {
      let isCapsLock = false;
      if (event.getModifierState && event.getModifierState('CapsLock')) {
        isCapsLock = true;
      }
      if (isCapsLock !== this.capsLock) {
        this.setCapsLock(isCapsLock);
      }
      const key = event.key || event;
      if (this.keyNotUsedInActivity(key, event)) return;

      this.removeFromActiveKeys({ event });

      if (this.keyboardLocked) return;
      this.resetAttemptedKey();
    },

    isShiftKey(keyPressed) {
      const key = keyPressed.toLowerCase();
      // iOS registers Shift as 'Dead'
      return key.includes('shift') || key === 'dead';
    },

    normalizeKey(key, location = '') {
      const normalized = {
        enter: 'enter',
        spacebar: ' ',
        quotationmark: '"',
        shift: `shift${location}`,
        tab: 'tab'
      };

      return normalized[key.toLowerCase()] || key;
    }
  }
};
</script>
